/**
 * Blocos da tela de VISUALIZAÇÃO (detalhes de um registro).
 *
 * Extraídos de `EmployeeDetailsPage`, que é a referência do padrão. Antes viviam
 * locais dentro daquela página, então cada outra tela de detalhes inventava a
 * própria forma de mostrar "rótulo: valor" — a mesma origem das 24 tabelas
 * divergentes, num lugar novo.
 *
 * Uma tela de visualização se monta com três peças, nesta ordem:
 *
 *   <PageShell title={nome do registro} actions={[Voltar, Editar]}>
 *     <IdentityBanner ... />          faixa de identidade do registro
 *     <StatCard ... /> × N            números/datas de destaque, em linha
 *     <SectionCard ...>               blocos temáticos
 *       <InfoItem ... /> × N            rótulo + valor dentro do bloco
 *     </SectionCard>
 *   </PageShell>
 *
 * @example
 * <SectionCard title="Contato & Endereço" icon={<PhoneIcon />} accentColor={theme.palette.primary.main}>
 *   <Grid container spacing={2}>
 *     <Grid item xs={12} sm={6}><InfoItem label="WhatsApp" value={emp.phone} icon={<WhatsAppIcon />} /></Grid>
 *     <Grid item xs={12} sm={6}><InfoItem label="E-mail" value={emp.email} /></Grid>
 *   </Grid>
 * </SectionCard>
 */

import React from 'react';
import { alpha, Avatar, Box, Card, CardContent, Chip, Paper, Typography, useTheme } from '@mui/material';

/* ─────────────── rótulo + valor ─────────────── */

export interface InfoItemProps {
  label: string;
  value: React.ReactNode;
  /** Ícone pequeno antes do rótulo. */
  icon?: React.ReactNode;
  /** Ocupa a linha inteira do grid (endereço, observações). */
  fullWidth?: boolean;
}

/** Par rótulo/valor. Valor vazio vira travessão — nunca deixe a linha em branco. */
export const InfoItem: React.FC<InfoItemProps> = ({ label, value, icon, fullWidth }) => (
  <Box sx={{ minWidth: 0, flex: fullWidth ? '1 1 100%' : undefined }}>
    <Box display="flex" alignItems="center" gap={0.5} mb={0.25}>
      {icon && (
        <Box sx={{ color: 'text.secondary', display: 'flex', fontSize: 16 }}>{icon}</Box>
      )}
      <Typography variant="caption" color="text.secondary" fontWeight={500} noWrap>
        {label}
      </Typography>
    </Box>
    {/*
      `component="div"` e não o `<p>` padrão do body2: a prop `value` é
      `ReactNode`, e telas legitimamente passam um `Chip` de status ou um `Box`
      com botões. Dentro de `<p>` isso vira `<div>` aninhado em parágrafo — HTML
      inválido, que o React acusa como erro de hidratação. Só apareceu rodando
      a tela de empresa no browser, depois de `tsc`, `eslint` e build limpos.
    */}
    <Typography variant="body2" component="div" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
      {value || '—'}
    </Typography>
  </Box>
);

/* ─────────────── bloco temático ─────────────── */

export interface SectionCardProps {
  title: string;
  icon: React.ReactNode;
  /** Borda superior colorida que identifica o bloco. */
  accentColor?: string;
  /** Contador à direita do título (nº de registros relacionados). */
  badge?: React.ReactNode;
  /**
   * Controle no canto direito do cabeçalho: botão "Editar", menu, switch.
   * Ocupa o mesmo canto do `badge` e os dois convivem — o contador primeiro,
   * o controle depois.
   */
  action?: React.ReactNode;
  children: React.ReactNode;
}

/** Card de um assunto: "Contato & Endereço", "Dados Ocupacionais", "Treinamentos". */
export const SectionCard: React.FC<SectionCardProps> = ({
  title,
  icon,
  accentColor,
  badge,
  action,
  children,
}) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
      borderTop: accentColor ? `3px solid ${accentColor}` : undefined,
    }}
  >
    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Box sx={{ color: accentColor || 'primary.main', display: 'flex' }}>{icon}</Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
        {(badge || action) && (
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            {badge}
            {action}
          </Box>
        )}
      </Box>
      {children}
    </CardContent>
  </Card>
);

/* ─────────────── destaque numérico ─────────────── */

export interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  /** Linha de apoio sob o valor: "41 anos", "6 anos e 6 meses". */
  sublabel?: string;
  color?: string;
}

/** Tile compacto para o dado que o usuário procura primeiro. Use de 3 a 5 por tela. */
export const StatCard: React.FC<StatCardProps> = ({ icon, label, value, sublabel, color }) => {
  const theme = useTheme();
  const c = color || theme.palette.primary.main;
  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(c, 0.1),
          color: c,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={700} noWrap>
          {value}
        </Typography>
        {sublabel && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {sublabel}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

/* ─────────────── faixa de identidade ─────────────── */

export interface IdentityBannerProps {
  /** Iniciais ou ícone dentro do avatar. */
  avatar: React.ReactNode;
  /**
   * Nome do registro, na primeira linha da faixa, à esquerda do `status`.
   * É o ÚNICO lugar onde ele aparece: o `title` do `PageShell` fica com o nome
   * genérico da entidade ("Empresa", "Usuário", "Fatura"). Sem isto a faixa
   * abria pelo chip de status e não se sabia de que registro ela era.
   * Decisão do Roberto em 13/08/2026, olhando as telas no browser.
   */
  name?: React.ReactNode;
  /** Chip de situação do registro (Ativo, Inativo, Cancelado). */
  status?: React.ReactNode;
  /** Hierarquia/contexto: "MetalSP › Recursos Humanos › Auxiliar Administrativo". */
  context?: React.ReactNode;
  /** Identificador e atalhos: CPF/CNPJ, botões de WhatsApp e e-mail. */
  identifiers?: React.ReactNode;
  color?: string;
}

/**
 * Faixa logo abaixo do cabeçalho, com a identidade do registro.
 * A primeira linha é o `name` — o mesmo texto do `title` do `PageShell`.
 */
export const IdentityBanner: React.FC<IdentityBannerProps> = ({
  avatar,
  name,
  status,
  context,
  identifiers,
  color,
}) => {
  const theme = useTheme();
  const c = color || theme.palette.primary.main;
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2.5,
        borderLeft: `4px solid ${c}`,
        bgcolor: alpha(c, 0.03),
      }}
    >
      <Avatar sx={{ width: 72, height: 72, bgcolor: c, fontSize: 24, fontWeight: 700 }}>
        {avatar}
      </Avatar>
      {/*
        `alignItems: 'flex-start'` é obrigatório aqui. Sem ele a coluna usa o
        `stretch` padrão do flex e o `Chip` de status estica para a largura
        inteira da faixa — uma barra colorida ocupando o lugar onde o olho
        procura a identidade do registro. Passou despercebido porque a tela de
        referência ainda monta a própria faixa e nunca exercitou este bloco.
      */}
      <Box
        sx={{
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.75,
        }}
      >
        {(name || status) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {name && (
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ lineHeight: 1.2, wordBreak: 'break-word' }}
              >
                {name}
              </Typography>
            )}
            {status}
          </Box>
        )}
        {context && (
          <Typography variant="body2" color="text.secondary" component="div">
            {context}
          </Typography>
        )}
        {identifiers && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {identifiers}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

/** Chip monoespaçado para CPF/CNPJ/matrícula dentro do `IdentityBanner`. */
export const IdentifierChip: React.FC<{ label: string; icon?: React.ReactElement }> = ({
  label,
  icon,
}) => <Chip size="small" icon={icon} label={label} sx={{ fontFamily: 'monospace' }} />;

export default SectionCard;
